import {MutableRefObject, useRef, useState} from 'react';
import {CaregiverMode} from '../../enums';
import {usePaginatedTable} from '../../hooks';
import {translations} from '../../i18n/translations';
import {ApplicationsRepository} from '../../repositories/applications';
import {PageHeader} from '../page-header/page-header';
import {PaginatedTable} from '../paginated-table/paginated-table';
import {CategorizedApplication} from '../../backend/responses/categorized-application';
import {PaginatedTableData} from '../../interfaces/paginated-table-data';
import {useTranslation} from 'react-i18next';
import {useSnackBar} from '../../contexts/snack-bar/context';
import {ApplicationsStateModal} from './modals/applications-state-modal';
import {ColumnIdentifier} from './user-applications';

type UserApplicationsTableProps = {
  caregiverMode: CaregiverMode.Professional | CaregiverMode.SystemAdministrator;
  canWrite: boolean;
  userId: string;
  userName: string;
  entity?: {
    entityId: string;
    entityName?: string;
  };
  getApplications: (
    limit: number,
    skip: number,
    search?: string | undefined
  ) => Promise<{
    data: PaginatedTableData<CategorizedApplication>;
    totalCount: number;
  }>;
};
export const UserApplicationsTable = ({
  caregiverMode,
  canWrite,
  userId,
  userName,
  entity,
  getApplications,
}: UserApplicationsTableProps) => {
  const snackBarContext = useSnackBar();
  const [updateApplicationDialogOpened, setUpdateApplicationDialogOpened] = useState<{
    disableDialogOpened: boolean;
    enableDialogOpened: boolean;
  }>({disableDialogOpened: false, enableDialogOpened: false});
  const dataToUpdateRef: MutableRefObject<
    {applicationId: string; applicationName: string; applicationActiveFlag: boolean} | undefined
  > = useRef();
  const {t} = useTranslation();

  // Use the paginated table hook
  const {
    data,
    isLoading,
    error,
    pagesCount,
    currentPage,
    currentSearch,
    handlePageChange,
    handleUpdateCurrentSearch,
    handleApplyFilters,
    handleUpdateItem,
  } = usePaginatedTable<CategorizedApplication>({
    fetchData: getApplications,
  });

  const handleAskUpdateApplicationState = (applicationId: string, value: boolean) => {
    // Get application name
    const applicationName: string | undefined = data
      .find((obj) => obj.id === applicationId)
      ?.columns.find((obj) => obj.columnId === ColumnIdentifier.Name)?.value;

    // Set application info for later
    dataToUpdateRef.current = {
      applicationId: applicationId,
      applicationName: applicationName || '',
      applicationActiveFlag: value,
    };

    // Open modal
    setUpdateApplicationDialogOpened({
      disableDialogOpened: !value,
      enableDialogOpened: value,
    });
  };

  const handleUpdateApplicationState = () => {
    if (dataToUpdateRef.current) {
      // Keep values
      const applicationId = dataToUpdateRef.current.applicationId;
      const value = dataToUpdateRef.current.applicationActiveFlag;

      // Clear
      dataToUpdateRef.current = undefined;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === applicationId)?.item;
      if (updatedItem) {
        updatedItem.application.isActive = value;
      }

      // Update item in UI
      handleUpdateItem(
        applicationId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: value
              ? t(translations.user_applications.state_value_true)
              : t(translations.user_applications.state_value_false),
          },
        ],
        updatedItem,
        value
      );

      // Make request to the backend
      // Note: Don't await because it will take too much time to update
      continueUpdateApplicationState(userId, applicationId, value);
    } else {
      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: t(translations.user_applications.errors.update_state_application_error_message),
      });

      // Not expected. Log.
      console.debug(
        '🔴 Could not update the state of the application. Information was missing to perform the request.'
      );
    }
  };

  const continueUpdateApplicationState = async (
    userId: string,
    applicationId: string,
    value: boolean
  ) => {
    try {
      // Make request to the backend
      await ApplicationsRepository.instance.updateActiveStateForUser(userId, applicationId, value);
    } catch (error) {
      // Old flag
      const oldFlag: boolean = !value;

      // Updated item
      let updatedItem = data.find((obj) => obj.id === applicationId)?.item;
      if (updatedItem) {
        updatedItem.application.isActive = oldFlag;
      }

      // Change item back
      handleUpdateItem(
        applicationId,
        [
          {
            updatedColumnId: ColumnIdentifier.State,
            updatedColumnValue: oldFlag
              ? t(translations.user_applications.state_value_true)
              : t(translations.user_applications.state_value_false),
          },
        ],
        updatedItem,
        oldFlag
      );

      // Error
      snackBarContext.setOfficeToastNotification({
        severity: 'error',
        message: value
          ? t(translations.user_applications.errors.enable_application_error_message)
          : t(translations.user_applications.errors.disable_application_error_message),
      });

      // Log
      console.debug(
        `🔴 Could not update the state of the application with id ${applicationId} to ${value} for user with id ${userId}.`,
        error
      );
    }
  };

  const modalState: 'disable' | 'enable' = updateApplicationDialogOpened.disableDialogOpened
    ? 'disable'
    : 'enable';

  return (
    <>
      {caregiverMode === CaregiverMode.SystemAdministrator && (
        <PageHeader
          showSearch={true}
          searchLabel={t(translations.user_applications.search_input_placeholder)}
          searchText={currentSearch}
          searchOnChange={(text: string) => {
            // Update text search
            handleUpdateCurrentSearch(text);
          }}
          searchOnSubmit={() => {
            // Query backend about this
            handleApplyFilters(1, currentSearch);
          }}
          disableSearch={isLoading}
        />
      )}
      <PaginatedTable
        avatar={{
          type: 'other',
        }}
        emptyTableMessage={t(translations.user_applications.no_applications)}
        columns={[
          {
            columnId: ColumnIdentifier.Name,
            title: t(translations.user_applications.name_header),
            flexGrow: 1,
          },
          {
            columnId: ColumnIdentifier.Category,
            title: t(translations.user_applications.category_header),
            flexGrow: 1,
          },
          {
            columnId: ColumnIdentifier.State,
            title: t(translations.user_applications.state_header),
            flexGrow: 1,
            button: {
              type: 'toggle',
              showColumnValue: true,
              toggle: {
                enabledValue: t(translations.user_applications.state_value_true),
                handleClick: handleAskUpdateApplicationState,
              },
              disabled: !canWrite,
              disabledTooltip: t(translations.user_applications.disabled_state_tooltip),
            },
          },
        ]}
        rows={data}
        isLoading={isLoading}
        error={error}
        pagesCount={pagesCount}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        linkableRows={false}
      />
      <ApplicationsStateModal
        open={
          updateApplicationDialogOpened.disableDialogOpened ||
          updateApplicationDialogOpened.enableDialogOpened
        }
        onClose={() => {
          setUpdateApplicationDialogOpened({
            disableDialogOpened: false,
            enableDialogOpened: false,
          });

          dataToUpdateRef.current = undefined;
        }}
        onConfirm={() => {
          setUpdateApplicationDialogOpened({
            disableDialogOpened: false,
            enableDialogOpened: false,
          });

          handleUpdateApplicationState();
        }}
        state={modalState}
        applicationName={dataToUpdateRef.current?.applicationName || ''}
        description={
          caregiverMode === CaregiverMode.SystemAdministrator
            ? t(
                translations.user_applications[
                  `${modalState}_application_for_entity_dialog_message`
                ],
                {
                  name: userName,
                  entity: entity?.entityName || '',
                }
              )
            : t(translations.user_applications[`${modalState}_application_dialog_message`], {
                name: userName,
              })
        }
      />
    </>
  );
};
